/**
 * Admin API client.
 *
 * All admin endpoints sit behind the Cognito JWT authorizer, so every request includes
 * the bearer access token. 401 responses trigger an automatic refresh-and-retry; if the
 * refresh also fails, the session is cleared and the user bounced to the login flow.
 */
import type {
  AppUser,
  AppUserCreateRequest,
  AppUserProfileUpdate,
  AppUserStatus,
} from '@obd-reader/shared/appUser';
import type {
  GetScanAnalysisSettingsResponse,
  ListPriorAnalysesResponse,
  ListScanAnalysisQueueResponse,
  PriorAnalysisSummary,
  ManuallyEnqueueScanRequest,
  ManuallyEnqueueScanResponse,
  ResolveProposedAppChangeRequest,
  ResolveProposedAppChangeResponse,
  ScanAnalysisMode,
  ScanAnalysisQueueItem,
  ScanAnalysisReview,
  ScanAnalysisReviewRequest,
  ScanAnalysisReviewResponse,
  ScanAnalysisReviewStatus,
  ScanAnalysisSettings,
  UpdateScanAnalysisSettingsRequest,
  UpdateScanAnalysisSettingsResponse,
} from '@obd-reader/shared';
import { getValidIdToken, refreshAccessToken } from './auth';
import { getConfig } from './config';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const config = getConfig();
  const url = `${config.apiBaseUrl}${path}`;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    };
    if (token) headers.authorization = `Bearer ${token}`;
    if (init.body && !headers['content-type']) headers['content-type'] = 'application/json';
    return fetch(url, { ...init, headers });
  };

  let token = await getValidIdToken();
  let response = await send(token);

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      token = await getValidIdToken();
      response = await send(token);
    }
    if (response.status === 401) {
      sessionStorage.clear();
      window.location.assign('/login');
      throw new ApiError(401, 'Unauthorized');
    }
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

// ── Scans ───────────────────────────────────────────

export interface AdminScanSummary {
  scanId: string;
  scanShortcodeBody: string | null;
  scannedAt: string;
  createdAt: string;
  vin: string | null;
  make: string | null;
  model: string | null;
  modelYear: number | null;
  overallStatus: string | null;
  reportUrl: string | null;
  reportUrlLatest: string | null;
  batteryReportUrl: string | null;
  batteryReportUrlLatest: string | null;
  outcome: 'success' | 'failure';
  failureReason?: string;
  totalDtcs: number | null;
  scanOrigin: string | null;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  signalSetStats: import('@obd-reader/shared').SignalSetMetaStats | null;
}

export interface AdminScansResponse {
  scans: AdminScanSummary[];
  nextCursor?: string;
}

export interface ListScansParams {
  limit?: number;
  cursor?: string;
  outcome?: 'success' | 'failure';
  make?: string;
  model?: string;
  userId?: string;
}

export async function listScans(params: ListScansParams = {}): Promise<AdminScansResponse> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.outcome) qs.set('outcome', params.outcome);
  if (params.make) qs.set('make', params.make);
  if (params.model) qs.set('model', params.model);
  if (params.userId) qs.set('userId', params.userId);
  const q = qs.toString();
  return adminFetch<AdminScansResponse>(`/admin/scans${q ? `?${q}` : ''}`);
}

export interface AdminScanDetail {
  scanId: string;
  scanShortcodeBody: string | null;
  scannedAt: string;
  createdAt: string;
  outcome: 'success' | 'failure';
  vin: string | null;
  vehicleProfile: Record<string, unknown> | null;
  reportUrl: string | null;
  reportUrlLatest: string | null;
  batteryReportUrl: string | null;
  batteryReportUrlLatest: string | null;
  summary: Record<string, unknown> | null;
  enriched: Record<string, unknown> | null;
  failureReport?: Record<string, unknown>;
  review?: ScanAnalysisReview;
}

export async function getScan(scanId: string): Promise<AdminScanDetail> {
  return adminFetch<AdminScanDetail>(`/admin/scans/${encodeURIComponent(scanId)}`);
}

export async function getPriorAnalyses(scanId: string): Promise<ListPriorAnalysesResponse> {
  return adminFetch<ListPriorAnalysesResponse>(
    `/admin/scans/${encodeURIComponent(scanId)}/prior-analyses`,
  );
}

export type { ListPriorAnalysesResponse, PriorAnalysisSummary };

// ── Vehicle KB ───────────────────────────────────────

export interface SignalSetStats {
  sampleCount: number;
  totalReadingsReturned: number;
  lastReadingCount: number;
  lastSampleAt: string;
  averageReadingCount: number;
}

export interface AdminKbProfile {
  vehicleKey: string;
  make?: string;
  model?: string;
  modelYear?: number;
  sampleCount: number;
  lastUpdatedAt: string;
  workingCommandCount: number;
  failingAddressCount: number;
  signalSetStats?: SignalSetStats;
}

export interface AdminKbListResponse {
  profiles: AdminKbProfile[];
  nextCursor?: string;
}

export async function listKb(cursor?: string, limit = 50): Promise<AdminKbListResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  return adminFetch<AdminKbListResponse>(`/admin/vehicle-kb?${qs.toString()}`);
}

export interface AdminKbDetail {
  vehicleKey: string;
  profile: {
    sampleCount: number;
    lastUpdatedAt: string;
    workingCommands: Array<{
      header: string;
      receiveFilter?: string;
      mode: string;
      pid: string;
      successCount: number;
      tries: number;
      lastSuccessAt: string;
      lastAttemptedAt: string;
      wilsonScore: number;
    }>;
    failingAddresses: Array<{
      header: string;
      successCount: number;
      tries: number;
      lastAttemptedAt: string;
      lastFailAt: string;
    }>;
    signalSetStats?: SignalSetStats;
  } | null;
}

export async function getKbProfile(vehicleKey: string): Promise<AdminKbDetail> {
  return adminFetch<AdminKbDetail>(`/admin/vehicle-kb/${encodeURIComponent(vehicleKey)}`);
}

// ── Aggregations ─────────────────────────────────────

export interface AggregationRow {
  vehicleKey: string;
  make: string | null;
  model: string | null;
  modelYear: number | null;
  totalScans: number;
  successScans: number;
  failureScans: number;
  successRate: number;
}

export interface AggregationsResponse {
  rows: AggregationRow[];
  generatedAt: string;
  windowDays: number;
}

export async function getAggregations(windowDays = 30): Promise<AggregationsResponse> {
  return adminFetch<AggregationsResponse>(`/admin/aggregations?windowDays=${windowDays}`);
}

// ── App users (mobile users) ─────────────────────────

export interface ListAppUsersParams {
  status?: AppUserStatus;
  cursor?: string;
  limit?: number;
}

export interface ListAppUsersResponse {
  users: AppUser[];
  nextCursor?: string;
}

export async function listAppUsers(params: ListAppUsersParams = {}): Promise<ListAppUsersResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return adminFetch<ListAppUsersResponse>(`/admin/app-users${q ? `?${q}` : ''}`);
}

export async function getAppUser(userId: string): Promise<{ user: AppUser }> {
  return adminFetch<{ user: AppUser }>(`/admin/app-users/${encodeURIComponent(userId)}`);
}

export async function createAppUser(body: AppUserCreateRequest): Promise<{ user: AppUser }> {
  return adminFetch<{ user: AppUser }>(`/admin/app-users`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateAppUserProfile(
  userId: string,
  body: AppUserProfileUpdate,
): Promise<{ user: AppUser }> {
  return adminFetch<{ user: AppUser }>(`/admin/app-users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function updateAppUserStatus(
  userId: string,
  status: AppUserStatus,
): Promise<{ user: AppUser }> {
  return adminFetch<{ user: AppUser }>(`/admin/app-users/${encodeURIComponent(userId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export type { AppUser, AppUserStatus, AppUserCreateRequest, AppUserProfileUpdate };

// ── Scan analysis review queue (PR H) ────────────────

export interface ListScanAnalysisQueueParams {
  status?: ScanAnalysisReviewStatus;
  cursor?: string;
  limit?: number;
}

export async function listScanAnalysisQueue(
  params: ListScanAnalysisQueueParams = {},
): Promise<ListScanAnalysisQueueResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return adminFetch<ListScanAnalysisQueueResponse>(`/admin/scan-analysis-queue${q ? `?${q}` : ''}`);
}

export async function updateScanAnalysisReview(
  scanId: string,
  body: ScanAnalysisReviewRequest,
): Promise<ScanAnalysisReviewResponse> {
  return adminFetch<ScanAnalysisReviewResponse>(
    `/admin/scan-analysis-queue/${encodeURIComponent(scanId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function resolveProposedAppChange(
  scanId: string,
  index: number,
  body: ResolveProposedAppChangeRequest,
): Promise<ResolveProposedAppChangeResponse> {
  return adminFetch<ResolveProposedAppChangeResponse>(
    `/admin/scan-analysis-queue/${encodeURIComponent(scanId)}/proposed-app-changes/${index}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export type {
  ResolveProposedAppChangeRequest,
  ResolveProposedAppChangeResponse,
  ScanAnalysisQueueItem,
  ScanAnalysisReviewRequest,
  ScanAnalysisReviewResponse,
  ScanAnalysisReviewStatus,
};

// ── Scan-analysis settings + manual enqueue ───────────

export async function getScanAnalysisSettings(): Promise<ScanAnalysisSettings> {
  const res = await adminFetch<GetScanAnalysisSettingsResponse>('/admin/scan-analysis-settings');
  return res.settings;
}

export async function updateScanAnalysisSettings(
  triggerMode: ScanAnalysisMode,
): Promise<ScanAnalysisSettings> {
  const body: UpdateScanAnalysisSettingsRequest = { triggerMode };
  const res = await adminFetch<UpdateScanAnalysisSettingsResponse>(
    '/admin/scan-analysis-settings',
    { method: 'PATCH', body: JSON.stringify(body) },
  );
  return res.settings;
}

export async function manuallyEnqueueScanForAnalysis(
  scanId: string,
  reason?: string,
): Promise<ManuallyEnqueueScanResponse> {
  const body: ManuallyEnqueueScanRequest = {};
  if (reason && reason.trim()) body.reason = reason.trim();
  return adminFetch<ManuallyEnqueueScanResponse>(
    `/admin/scans/${encodeURIComponent(scanId)}/analyse`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export type {
  ScanAnalysisMode,
  ScanAnalysisSettings,
  ManuallyEnqueueScanRequest,
  ManuallyEnqueueScanResponse,
};

// App-change digest API/page removed in PR #120 — its aggregations were a
// duplicate of the per-scan Analysis Review tab. The /admin/app-change-digest
// endpoint and ListAppChangeDigestResponse type were also removed.
